"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useRouter } from "next/navigation"
import PageHero from "@/components/page-hero"
import { pageHeroData } from "@/data/page-hero-data"
import { api, type BoerseSlot, type BoerseSlotsResponse, type BoersePublicConfig } from "@/lib/api"
import { formatBerlinTime, formatBerlinDateLong, formatPrice } from "@/lib/berlin-time"
import { describeDiscountConditions, describeDiscountEffect } from "@sw-business-solutions/cst-mobikart-core/lib/pricing-conditions"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useBoerseSocket } from "@/hooks/use-boerse-socket"
import { fileLog } from "@/lib/file-logger"
import {
  Clock, Users, TrendingDown, Timer, ArrowRight,
  RefreshCw, AlertCircle, Zap, ChevronDown, ChevronUp, Loader2, X, ArrowUpDown, Lock,
  BadgePercent, Info,
} from "lucide-react"
import Link from "next/link"

// ── Countdown-Hook ──

function useCountdown(targetSeconds: number | null, onExpire?: () => void) {
  const [seconds, setSeconds] = useState(targetSeconds ?? 0)
  const onExpireRef = useRef(onExpire)
  onExpireRef.current = onExpire

  useEffect(() => {
    if (targetSeconds === null || targetSeconds <= 0) {
      setSeconds(0)
      return
    }
    setSeconds(targetSeconds)
    const iv = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(iv)
          // Drop ist abgelaufen → Preise sofort neu laden
          setTimeout(() => onExpireRef.current?.(), 500)
          return 0
        }
        return s - 1
      })
    }, 1_000)
    return () => clearInterval(iv)
  }, [targetSeconds])

  const min = Math.floor(seconds / 60)
  const sec = seconds % 60
  return { seconds, display: `${min}:${String(sec).padStart(2, "0")}` }
}

// ── Slot-Card ──

function BoerseSlotCard({
  slot, maxSeats, onRefresh, activeSlotId, onActivate, onDeactivate, onHoldReleased, isExternallyHeld,
}: {
  slot: BoerseSlot; maxSeats: number; onRefresh: () => void
  activeSlotId: string | null
  onActivate: (slotId: string, holdId: string, seats: number) => void
  onDeactivate: () => void
  onHoldReleased: (slotId: string) => void
  isExternallyHeld: boolean
}) {
  const router = useRouter()
  // Nur null/undefined = wirklich letzter Drop (kein weiterer kommt).
  // 0 bedeutet "Drop gerade jetzt" — es können weitere folgen!
  const isLastDrop = slot.nextDropInSeconds == null
  const countdown = useCountdown(slot.nextDropInSeconds ?? null, onRefresh)
  const [seats, setSeats] = useState(1)
  const seatsRef = useRef(1) // Ref für Debounce (stale-closure-safe)
  const [holdLoading, setHoldLoading] = useState(false)
  const [holdError, setHoldError] = useState<string | null>(null)
  const holdIdRef = useRef<string | null>(null)
  const holdCreatingRef = useRef(false) // Mutex: verhindert gleichzeitige Hold-Ops
  const holdUpdateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null) // Debounce
  const heldSeatsRef = useRef(0) // Tatsächlich auf dem Server gehaltene Sitze
  const slotRemainingRef = useRef(slot.remainingSeats) // Immer aktuell für Closures
  slotRemainingRef.current = slot.remainingSeats
  const selectedDurationRef = useRef<number | null>(null) // Ref für Debounce (stale-closure-safe)
  // Default: längste verfügbare Dauer vorauswählen (höheres Fahrerlebnis,
  // besserer Umsatz). Bei bereits gebuchter Slot-Duration diese fixieren.
  const longestAvailableDuration = slot.availableDurations?.length
    ? Math.max(...slot.availableDurations)
    : null
  const initDuration = slot.bookedRideDurationMin ?? longestAvailableDuration ?? null
  const [selectedDuration, setSelectedDuration] = useState<number | null>(initDuration)
  // Ref sofort initialisieren
  if (selectedDurationRef.current === null && initDuration !== null) {
    selectedDurationRef.current = initDuration
  }

  const isActive = activeSlotId === slot.slotId
  const isLocked = activeSlotId !== null && activeSlotId !== slot.slotId

  // Sync duration when backend reports a locked duration (e.g. after WS refresh)
  useEffect(() => {
    if (slot.bookedRideDurationMin != null && slot.bookedRideDurationMin > 0) {
      setSelectedDuration(slot.bookedRideDurationMin)
      selectedDurationRef.current = slot.bookedRideDurationMin
    }
  }, [slot.bookedRideDurationMin])

  // maxBookable: eigene gehaltene Sitze zurückrechnen (wie buchen-Seite).
  // Nur heldSeatsRef (eigene Sitze) verwenden — slot.capacityHeld ist die
  // GESAMT-Held aller User und würde bei Multi-User zu hohe Werte liefern.
  // Math.min(capacityTotal) cappt Überbestimmungen während kurzer Inkonsistenzen.
  const ownHeld = isActive ? heldSeatsRef.current : 0
  const adjustedRemaining = isActive
    ? Math.min(slot.capacityTotal, slot.remainingSeats + ownHeld)
    : slot.remainingSeats
  const maxBookable = maxSeats > 0 ? Math.min(maxSeats, adjustedRemaining) : adjustedRemaining

  // Auto-clamp seats when remainingSeats decreases (live WS updates).
  // ABER: wenn der Server unseren Hold bestätigt hat (holdIdRef + heldSeatsRef),
  // NICHT clampen — unsere Sitze sind serverseitig garantiert. Kurze
  // API-Inkonsistenzen (stale fetch) dürfen keinen Cascade auslösen.
  useEffect(() => {
    const LOG_PREFIX = `[BOERSE:Card:${slot.slotId.slice(-4)}]`
    fileLog(`${LOG_PREFIX} 🔄 auto-clamp useEffect triggered`)
    fileLog(`${LOG_PREFIX}   seats: ${seats}, maxBookable: ${maxBookable}`)
    fileLog(`${LOG_PREFIX}   holdIdRef.current: ${holdIdRef.current}`)
    fileLog(`${LOG_PREFIX}   heldSeatsRef.current: ${heldSeatsRef.current}`)
    fileLog(`${LOG_PREFIX}   slot.remainingSeats: ${slot.remainingSeats}`)
    fileLog(`${LOG_PREFIX}   slot.capacityTotal: ${slot.capacityTotal}`)
    fileLog(`${LOG_PREFIX}   slot.capacityHeld: ${slot.capacityHeld}`)

    if (holdIdRef.current && seats <= heldSeatsRef.current) {
      fileLog(`${LOG_PREFIX} ⚠️ AUTO-CLAMP SKIPPED: holdIdRef exists AND seats(${seats}) <= heldSeatsRef(${heldSeatsRef.current})`)
      return
    }
    if (maxBookable > 0 && seats > maxBookable) {
      fileLog(`${LOG_PREFIX} 📉 AUTO-CLAMP: seats=${seats} → ${maxBookable}`)
      fileLog(`${LOG_PREFIX}   Reason: remaining=${slot.remainingSeats}, ownHeld=${heldSeatsRef.current}, cap=${slot.capacityTotal}, maxSeats(config)=${maxSeats}`)
      setSeats(maxBookable)
      seatsRef.current = maxBookable
    } else {
      fileLog(`${LOG_PREFIX} ✓ AUTO-CLAMP: no action needed`)
    }
  }, [maxBookable]) // eslint-disable-line react-hooks/exhaustive-deps

  const isVariable = slot.slotMode === "VARIABLE" && !!slot.availableDurations?.length
  const isLockedDuration = !!slot.bookedRideDurationMin

  // ── Hold erstellen (mit Mutex gegen gleichzeitige Aufrufe) ──
  const createHoldForSlot = useCallback(async (seatCount: number, duration: number | null) => {
    const LOG_PREFIX = `[BOERSE:Card:${slot.slotId.slice(-4)}]`
    fileLog(`${LOG_PREFIX} ══════════════════════════════════════════`)
    fileLog(`${LOG_PREFIX} createHoldForSlot CALLED`)
    fileLog(`${LOG_PREFIX}   seatCount: ${seatCount}`)
    fileLog(`${LOG_PREFIX}   duration: ${duration}`)
    fileLog(`${LOG_PREFIX}   holdIdRef.current: ${holdIdRef.current}`)
    fileLog(`${LOG_PREFIX}   heldSeatsRef.current: ${heldSeatsRef.current}`)
    fileLog(`${LOG_PREFIX}   seatsRef.current: ${seatsRef.current}`)
    fileLog(`${LOG_PREFIX}   slot.remainingSeats: ${slot.remainingSeats}`)
    fileLog(`${LOG_PREFIX}   slot.capacityTotal: ${slot.capacityTotal}`)
    fileLog(`${LOG_PREFIX}   slot.capacityHeld: ${slot.capacityHeld}`)
    fileLog(`${LOG_PREFIX}   maxSeats (config): ${maxSeats}`)
    fileLog(`${LOG_PREFIX}   maxBookable (computed): ${maxBookable}`)
    fileLog(`${LOG_PREFIX}   isActive: ${isActive}`)
    fileLog(`${LOG_PREFIX}   holdCreatingRef.current: ${holdCreatingRef.current}`)

    if (holdCreatingRef.current) {
      fileLog(`${LOG_PREFIX} ⚠️ MUTEX BLOCKED - Hold creation already in progress`)
      return null // Mutex: schon in Bearbeitung
    }
    holdCreatingRef.current = true
    setHoldLoading(true)
    setHoldError(null)

    // WS-Suppression: onActivate mit leerem holdId → activeHoldIdRef = null,
    // activeSlotIdRef = slotId. In diesem Zustand unterdrückt useBoerseSocket
    // ALLE Hold-Events für diesen Slot (Slot-Level-Suppression).
    // Das überbrückt das Zeitfenster zwischen cancel(alt) + create(neu),
    // in dem das WS-Event mit der neuen holdId eintrifft BEVOR die HTTP-Response
    // den neuen holdId zurückgibt.
    fileLog(`${LOG_PREFIX} 🔒 Activating WS-Suppression (slotId=${slot.slotId}, holdId="")`)
    onActivate(slot.slotId, "", seatCount)

    try {
      // Alt stornieren falls vorhanden
      if (holdIdRef.current) {
        fileLog(`${LOG_PREFIX} 🗑️ Cancelling OLD hold: ${holdIdRef.current}`)
        await api.cancelHold(holdIdRef.current).catch((e) => {
          fileLog("[WARN]",`${LOG_PREFIX} ⚠️ Cancel old hold failed:`, e)
        })
        fileLog(`${LOG_PREFIX} ✓ Old hold cancelled`)
        holdIdRef.current = null
      }

      fileLog(`${LOG_PREFIX} 📤 Creating NEW hold with ${seatCount} seats...`)
      const holdData = await api.createHold({
        trackId: slot.trackId,
        items: [{
          slotId: slot.slotId,
          seats: seatCount,
          type: "RIDE" as const,
          ...(isVariable && duration ? { rideDurationMin: duration } : {}),
        }],
        bookingSource: "SLOTBOERSE",
      })
      fileLog(`${LOG_PREFIX} ✅ NEW hold created:`, holdData._id)
      fileLog(`${LOG_PREFIX}   holdData.items:`, JSON.stringify(holdData.items))

      holdIdRef.current = holdData._id
      // Server-Response verwenden (wie buchen-Seite mit ownHeldMap),
      // nicht den lokalen Request-Wert → immer konsistent mit API.
      const heldItem = holdData.items.find((i) => i.slotId === slot.slotId)
      const newHeldSeats = heldItem?.seatsHeld ?? heldItem?.seats ?? seatCount
      fileLog(`${LOG_PREFIX}   heldItem.seatsHeld: ${heldItem?.seatsHeld}, heldItem.seats: ${heldItem?.seats}`)
      fileLog(`${LOG_PREFIX}   → Setting heldSeatsRef.current = ${newHeldSeats}`)
      heldSeatsRef.current = newHeldSeats
      onActivate(slot.slotId, holdData._id, heldSeatsRef.current)
      // Kapazitäten vom Server holen (epoch-guard verhindert stale Daten)
      fileLog(`${LOG_PREFIX} 🔄 Triggering onRefresh()`)
      onRefresh()
      return holdData
    } catch (err: unknown) {
      const LOG_PREFIX = `[BOERSE:Card:${slot.slotId.slice(-4)}]`
      fileLog("[ERROR]",`${LOG_PREFIX} ❌ createHold FAILED:`, err)
      fileLog(`${LOG_PREFIX}   Error message: ${err instanceof Error ? err.message : String(err)}`)
      fileLog(`${LOG_PREFIX}   Current state after failure:`)
      fileLog(`${LOG_PREFIX}     seatsRef.current: ${seatsRef.current}`)
      fileLog(`${LOG_PREFIX}     holdIdRef.current: ${holdIdRef.current}`)
      fileLog(`${LOG_PREFIX}     seatCount (requested): ${seatCount}`)

      // RETRY-LOGIK: Wenn die ursprüngliche Anfrage fehlschlug und der auto-clamp
      // inzwischen die Sitze reduziert hat (z.B. durch WS-Update während API-Call),
      // versuche einen neuen Hold mit der aktuellen (niedrigeren) Sitzanzahl.
      // Das verhindert, dass der Hold verloren geht wenn z.B. 7/8 angefordert
      // wurden aber nur noch 6 frei sind.
      const currentSeats = seatsRef.current
      fileLog(`${LOG_PREFIX} 🔍 RETRY check: currentSeats=${currentSeats}, seatCount=${seatCount}, holdIdRef=${holdIdRef.current}`)
      if (currentSeats > 0 && currentSeats < seatCount && !holdIdRef.current) {
        fileLog(`${LOG_PREFIX} 🔄 RETRY: Hold mit ${seatCount} fehlgeschlagen, Retry mit ${currentSeats}`)
        try {
          const retryHold = await api.createHold({
            trackId: slot.trackId,
            items: [{
              slotId: slot.slotId,
              seats: currentSeats,
              type: "RIDE" as const,
              ...(isVariable && duration ? { rideDurationMin: duration } : {}),
            }],
            bookingSource: "SLOTBOERSE",
          })
          fileLog(`${LOG_PREFIX} ✅ RETRY successful! holdId: ${retryHold._id}`)
          holdIdRef.current = retryHold._id
          const heldItem = retryHold.items.find((i) => i.slotId === slot.slotId)
          heldSeatsRef.current = heldItem?.seatsHeld ?? heldItem?.seats ?? currentSeats
          fileLog(`${LOG_PREFIX}   heldSeatsRef.current = ${heldSeatsRef.current}`)
          onActivate(slot.slotId, retryHold._id, heldSeatsRef.current)
          onRefresh()
          // Retry erfolgreich → early return
          return retryHold
        } catch (retryErr) {
          // Auch Retry fehlgeschlagen → normal weiter zum Error-Handling
          fileLog("[ERROR]",`${LOG_PREFIX} ❌ RETRY ALSO FAILED:`, retryErr)
        }
      } else {
        fileLog(`${LOG_PREFIX} ⚠️ RETRY skipped: conditions not met`)
        fileLog(`${LOG_PREFIX}   currentSeats > 0: ${currentSeats > 0}`)
        fileLog(`${LOG_PREFIX}   currentSeats < seatCount: ${currentSeats < seatCount}`)
        fileLog(`${LOG_PREFIX}   !holdIdRef.current: ${!holdIdRef.current}`)
      }

      const msg = err instanceof Error ? err.message : "Reservierung fehlgeschlagen – bitte erneut versuchen."
      fileLog(`${LOG_PREFIX} 💥 Setting holdError: ${msg}`)
      setHoldError(msg)
      // Kein Hold mehr aktiv (alter storniert, neuer gescheitert) → UI zurücksetzen
      if (!holdIdRef.current) {
        fileLog(`${LOG_PREFIX} 🔓 No holdIdRef → resetting UI (heldSeatsRef=0, onDeactivate, onRefresh)`)
        heldSeatsRef.current = 0
        onDeactivate()
        onRefresh()
      }
      return null
    } finally {
      fileLog(`[BOERSE:Card:${slot.slotId.slice(-4)}] ══════════════════════════════════════════`)
      fileLog(`[BOERSE:Card:${slot.slotId.slice(-4)}] createHoldForSlot FINISHED`)
      fileLog(`[BOERSE:Card:${slot.slotId.slice(-4)}]   holdIdRef.current: ${holdIdRef.current}`)
      fileLog(`[BOERSE:Card:${slot.slotId.slice(-4)}]   heldSeatsRef.current: ${heldSeatsRef.current}`)
      holdCreatingRef.current = false
      setHoldLoading(false)
    }
  }, [slot.trackId, slot.slotId, isVariable, maxSeats, maxBookable, isActive, onActivate, onDeactivate, onRefresh])

  // ── Hold stornieren (Karte schließen) ──
  const cancelCurrentHold = useCallback(async () => {
    const LOG_PREFIX = `[BOERSE:Card:${slot.slotId.slice(-4)}]`
    fileLog(`${LOG_PREFIX} 🗑️ cancelCurrentHold CALLED`)
    fileLog(`${LOG_PREFIX}   holdIdRef.current: ${holdIdRef.current}`)
    
    if (holdIdRef.current) {
      fileLog(`${LOG_PREFIX}   Cancelling hold: ${holdIdRef.current}`)
      await api.cancelHold(holdIdRef.current).catch((e) => {
        fileLog("[WARN]",`${LOG_PREFIX}   Cancel failed:`, e)
      })
      fileLog(`${LOG_PREFIX}   Hold cancelled`)
      holdIdRef.current = null
      // Inform page that this slot no longer has our hold
      onHoldReleased(slot.slotId)
    }
    fileLog(`${LOG_PREFIX}   Resetting state: seats=1, heldSeatsRef=0`)
    setSeats(1)
    seatsRef.current = 1
    heldSeatsRef.current = 0
    setHoldError(null)
    onDeactivate()
    fileLog(`${LOG_PREFIX}   Calling onRefresh()...`)
    await onRefresh() // AWAIT damit Kapazitäten korrekt angezeigt werden
    fileLog(`${LOG_PREFIX} ✓ cancelCurrentHold FINISHED`)
  }, [slot.slotId, onDeactivate, onHoldReleased, onRefresh])

  // Ref für createHoldForSlot → Debounce-Timer nutzt immer die aktuelle Version
  const createHoldRef = useRef(createHoldForSlot)
  useEffect(() => { createHoldRef.current = createHoldForSlot }, [createHoldForSlot])

  // Hold neu erstellen wenn Plätze oder Dauer sich ändern — DEBOUNCED (600ms)
  // So kann der User schnell +++ klicken ohne dass jeder Klick einen API-Call auslöst.
  // Refs statt Closures: verhindert stale-closure-Bugs wenn sich isActive/Deps
  // zwischen Klick und Debounce-Auslösung ändern.
  const handleSeatsChange = useCallback((newSeats: number) => {
    const LOG_PREFIX = `[BOERSE:Card:${slot.slotId.slice(-4)}]`
    fileLog(`${LOG_PREFIX} 👤 handleSeatsChange CALLED: ${seats} → ${newSeats}`)
    fileLog(`${LOG_PREFIX}   holdIdRef.current: ${holdIdRef.current}`)
    fileLog(`${LOG_PREFIX}   maxBookable: ${maxBookable}`)
    
    setSeats(newSeats)
    seatsRef.current = newSeats
    if (holdIdRef.current) {
      fileLog(`${LOG_PREFIX}   ⏱️ Starting 600ms debounce timer for createHold...`)
      if (holdUpdateTimerRef.current) {
        fileLog(`${LOG_PREFIX}   Clearing previous timer`)
        clearTimeout(holdUpdateTimerRef.current)
      }
      holdUpdateTimerRef.current = setTimeout(() => {
        holdUpdateTimerRef.current = null
        fileLog(`${LOG_PREFIX} ⏰ Debounce timer fired! seatsRef.current=${seatsRef.current}, selectedDurationRef.current=${selectedDurationRef.current}`)
        // seatsRef statt Closure-Wert: falls auto-clamp die Sitze zwischen
        // Klick und Debounce-Auslösung reduziert hat (z.B. anderer User hat
        // zwischenzeitlich Plätze reserviert → maxBookable gesunken), wird
        // der AKTUELLE (geclampte) Wert statt der veralteten Klick-Zahl genutzt.
        createHoldRef.current(seatsRef.current, selectedDurationRef.current)
      }, 600)
    } else {
      fileLog(`${LOG_PREFIX}   No holdIdRef → no debounced createHold`)
    }
  }, [slot.slotId, seats, maxBookable]) // Minimal deps für Logging

  const handleDurationChange = useCallback((dur: number) => {
    setSelectedDuration(dur)
    selectedDurationRef.current = dur
    if (holdIdRef.current) {
      if (holdUpdateTimerRef.current) clearTimeout(holdUpdateTimerRef.current)
      holdUpdateTimerRef.current = setTimeout(() => {
        holdUpdateTimerRef.current = null
        createHoldRef.current(seatsRef.current, dur)
      }, 600)
    }
  }, []) // Keine Deps — alles über Refs

  // Ref for onHoldReleased to avoid stale closures in cleanup
  const onHoldReleasedRef = useRef(onHoldReleased)
  useEffect(() => { onHoldReleasedRef.current = onHoldReleased }, [onHoldReleased])

  // Cleanup: Hold stornieren + Debounce-Timer aufräumen wenn Komponente unmounted
  useEffect(() => {
    const slotIdShort = slot.slotId.slice(-4)
    return () => {
      fileLog(`[BOERSE:Card:${slotIdShort}] 🧹 CLEANUP useEffect triggered (component unmounting)`)
      fileLog(`[BOERSE:Card:${slotIdShort}]   holdUpdateTimerRef.current: ${!!holdUpdateTimerRef.current}`)
      fileLog(`[BOERSE:Card:${slotIdShort}]   holdIdRef.current: ${holdIdRef.current}`)
      
      if (holdUpdateTimerRef.current) {
        fileLog(`[BOERSE:Card:${slotIdShort}]   Clearing debounce timer`)
        clearTimeout(holdUpdateTimerRef.current)
      }
      if (holdIdRef.current) {
        fileLog(`[BOERSE:Card:${slotIdShort}]   ⚠️ CANCELLING HOLD ON UNMOUNT: ${holdIdRef.current}`)
        api.cancelHold(holdIdRef.current).catch((e) => {
          fileLog("[WARN]",`[BOERSE:Card:${slotIdShort}]   Cancel on unmount failed:`, e)
        })
        // Inform page that this slot no longer has our hold
        onHoldReleasedRef.current(slot.slotId)
      } else {
        fileLog(`[BOERSE:Card:${slotIdShort}]   ✓ No hold to cancel (holdIdRef is null)`)
      }
    }
  }, [slot.slotId])

  // Effektive Preise: bei VARIABLE nach gewählter Dauer, sonst FIXED-Preise
  const effectiveRegularPrice =
    isVariable && selectedDuration && slot.durationPrices?.[selectedDuration] != null
      ? slot.durationPrices[selectedDuration]
      : slot.regularPrice
  const effectiveBoersePrice =
    isVariable && selectedDuration && slot.boerseDurationPrices?.[selectedDuration] != null
      ? slot.boerseDurationPrices[selectedDuration]
      : slot.boersePrice
  const savings = effectiveRegularPrice - effectiveBoersePrice

  return (
    <div className={`relative bg-white rounded-2xl shadow-lg border overflow-hidden transition-all duration-300 ${isLocked ? "opacity-50 pointer-events-none" : "border-gray-100 hover:shadow-xl"} ${isActive ? "ring-2 ring-[var(--main-color)]" : ""}`}>
      {/* Overlay: anderer User reserviert gerade */}
      {isExternallyHeld && !isActive && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
          <div className="text-center px-6 py-8">
            <Lock className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="text-base font-semibold text-gray-600">Wird gerade reserviert</p>
            <p className="text-sm text-gray-400 mt-1">Jemand anderes ist schneller gewesen. Wähle einen anderen Slot!</p>
          </div>
        </div>
      )}
      {/* Discount-Banner */}
      <div className={`${isLastDrop ? "bg-gradient-to-r from-red-500 to-red-600" : "bg-gradient-to-r from-[var(--main-color)] to-orange-500"} text-white px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          <span className="font-bold text-lg">−{Math.round(slot.discountPercent)}%</span>
        </div>
        {isLastDrop ? (
          <div className="flex items-center gap-1.5 text-sm bg-white/20 rounded-full px-3 py-1">
            <Zap className="h-4 w-4" />
            <span className="font-medium">Letzte Chance!</span>
          </div>
        ) : countdown.seconds > 0 ? (
          <div className="flex items-center gap-1.5 text-sm bg-white/20 rounded-full px-3 py-1">
            <Timer className="h-4 w-4" />
            <span className="font-mono font-medium">{countdown.display}</span>
            <span className="text-xs opacity-80">bis zum nächsten Drop</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-sm bg-white/20 rounded-full px-3 py-1 animate-pulse">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span className="font-medium text-xs">Preisupdate…</span>
          </div>
        )}
      </div>

      {/* Inhalte */}
      <div className="p-5">
        {/* Track + Zeit */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{slot.trackName}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{formatBerlinDateLong(slot.startAt)}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>{formatBerlinTime(slot.startAt)} – {formatBerlinTime(slot.endAt)}</span>
            </div>
            <span className="text-xs text-gray-400 mt-1 block">{slot.durationMin} Min.</span>
          </div>
        </div>

        {/* Preis */}
        <div className="flex items-end gap-3 mb-4">
          <div>
            <span className="text-sm text-gray-400 line-through">{formatPrice(effectiveRegularPrice)}</span>
            <div className="text-2xl font-extrabold text-[var(--main-color)]">{formatPrice(effectiveBoersePrice)}</div>
            <span className="text-xs text-gray-400">pro Person</span>
          </div>
          <div className="flex items-center gap-1 text-sm text-green-600 bg-green-50 rounded-full px-2.5 py-1">
            <TrendingDown className="h-4 w-4" />
            <span className="font-medium">Du sparst {formatPrice(savings)}</span>
          </div>
        </div>

        {/* Bedingte Rabatte (z.B. „ab 5 Personen"): Info-Badge mit Bedingungen.
            Wichtig: Diese Regeln gelten für REGULÄRE Buchungen — der Börsenpreis
            ersetzt den Preis komplett, deshalb der Hinweis im Popover. */}
        {slot.conditionalDiscounts && slot.conditionalDiscounts.length > 0 && (
          <div className="mb-4 -mt-1">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-200"
                >
                  <BadgePercent className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">Preisrabatte verfügbar</span>
                  <Info className="h-3.5 w-3.5 shrink-0 opacity-70" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3">
                <p className="mb-2 text-xs font-semibold">Mögliche Rabatte</p>
                <div className="space-y-2">
                  {slot.conditionalDiscounts.map((cd, idx) => (
                    <div key={idx} className="text-xs">
                      <p className="font-medium text-green-700">
                        {cd.label} · {describeDiscountEffect(cd.effect)}
                      </p>
                      <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-gray-600">
                        {describeDiscountConditions(cd.conditions).map((c) => (
                          <li key={c}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[10px] text-gray-400">
                  Gilt bei regulärer Buchung dieses Slots — nicht zusätzlich zum
                  Börsen-Rabatt.
                </p>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* VARIABLE: Dauer-Auswahl (immer sichtbar, nicht erst beim Aufklappen) */}
        {isVariable && slot.availableDurations && (
          <div className="mb-4">
            <span className="text-xs text-gray-600 font-medium flex items-center gap-1 mb-2">
              <Timer className="h-3.5 w-3.5" />
              Fahrtdauer wählen
              {isLockedDuration && (
                <span className="text-[10px] text-amber-600 ml-1">(festgelegt durch andere Buchung)</span>
              )}
            </span>
            <div className="flex gap-1.5 flex-wrap">
              {slot.availableDurations.map((dur) => {
                const isLockedDur = isLockedDuration && dur !== slot.bookedRideDurationMin
                const isDurActive = selectedDuration === dur
                const durBoersePrice = slot.boerseDurationPrices?.[dur]
                return (
                  <button
                    key={dur}
                    onClick={() => !isLockedDur && handleDurationChange(dur)}
                    disabled={isLockedDur}
                    className={[
                      "px-3 py-1.5 rounded-lg text-xs font-medium transition-all border",
                      isDurActive
                        ? "bg-[var(--main-color)] text-white border-[var(--main-color)]"
                        : isLockedDur
                          ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed line-through"
                          : "bg-white text-gray-700 border-gray-200 hover:border-[var(--main-color)] hover:text-[var(--main-color)]",
                    ].join(" ")}
                  >
                    {dur} Min
                    {durBoersePrice != null && durBoersePrice > 0 && (
                      <span className="ml-1 opacity-80">· {formatPrice(durBoersePrice)}</span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Plätze */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <Users className="h-4 w-4" />
          <span>
            {slot.remainingSeats} von {slot.capacityTotal} Plätzen frei
          </span>
          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--main-color)] rounded-full transition-all"
              style={{ width: `${Math.max(0, Math.min(100, ((slot.capacityTotal - slot.remainingSeats) / slot.capacityTotal) * 100))}%` }}
            />
          </div>
        </div>

        {/* Reservieren / Aufklappen */}
        {holdError && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-2">{holdError}</p>
        )}

        {!isActive ? (
          /* ── Noch nicht reserviert → "Jetzt reservieren" erstellt sofort einen Hold ── */
          <button
            onClick={() => createHoldForSlot(1, selectedDuration)}
            disabled={holdLoading || maxBookable <= 0 || isLocked || isExternallyHeld}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium text-[var(--main-color)] hover:bg-orange-50 rounded-xl py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {holdLoading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Wird reserviert…</>
            ) : (
              <>Jetzt reservieren <ChevronDown className="h-4 w-4" /></>
            )}
          </button>
        ) : (
          /* ── Reserviert → Plätze wählen + Weiter zur Buchung ── */
          <div className="border-t pt-4 mt-2 space-y-3 animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm text-green-700 bg-green-50 rounded-full px-3 py-1">
                <Zap className="h-3.5 w-3.5" />
                <span className="font-medium">Reserviert!</span>
              </div>
              <button
                onClick={cancelCurrentHold}
                className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg px-3 py-1.5 transition-colors"
              >
                <X className="h-4 w-4" />
                Abbrechen
              </button>
            </div>

            {/* Sitzplatzauswahl */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Plätze:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSeatsChange(Math.max(1, seats - 1))}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 text-lg font-medium disabled:opacity-40"
                  disabled={seats <= 1 || holdLoading}
                >
                  −
                </button>
                <span className="w-8 text-center font-bold text-lg">{seats}</span>
                <button
                  onClick={() => handleSeatsChange(Math.min(maxBookable, seats + 1))}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 text-lg font-medium disabled:opacity-40"
                  disabled={seats >= maxBookable || holdLoading}
                >
                  +
                </button>
              </div>
              <span className="text-sm text-gray-400">(max. {maxBookable})</span>
              {holdLoading && <Loader2 className="h-4 w-4 animate-spin text-[var(--main-color)]" />}
            </div>

            {/* Gesamtpreis */}
            <div className="flex items-center justify-between text-sm bg-gray-50 rounded-lg p-3">
              <span className="text-gray-600">Gesamtpreis{isVariable && selectedDuration ? ` (${selectedDuration} Min)` : ""}:</span>
              <div className="text-right">
                <span className="text-xs text-gray-400 line-through mr-2">{formatPrice(effectiveRegularPrice * seats)}</span>
                <span className="text-lg font-bold text-[var(--main-color)]">{formatPrice(effectiveBoersePrice * seats)}</span>
              </div>
            </div>

            {/* Weiter zur Buchung → Nur navigieren, Hold existiert bereits */}
            <button
              onClick={() => {
                const LOG_PREFIX = `[BOERSE:Card:${slot.slotId.slice(-4)}]`
                const hid = holdIdRef.current
                fileLog(`${LOG_PREFIX} 🚀 NAVIGATE TO BOOKING`)
                fileLog(`${LOG_PREFIX}   holdIdRef.current (before clear): ${hid}`)
                fileLog(`${LOG_PREFIX}   seats: ${seats}`)
                fileLog(`${LOG_PREFIX}   heldSeatsRef.current: ${heldSeatsRef.current}`)
                // holdIdRef leeren BEVOR navigiert wird: der Cleanup-Effect
                // (useEffect return) soll den Hold NICHT stornieren — der
                // Hold wird an die /buchen-Seite weitergegeben.
                holdIdRef.current = null
                fileLog(`${LOG_PREFIX}   holdIdRef.current (after clear): ${holdIdRef.current}`)
                const url = `/buchen?boerse=1&slotId=${slot.slotId}&seats=${seats}&trackId=${slot.trackId}${isVariable && selectedDuration ? `&duration=${selectedDuration}` : ""}&holdId=${hid}`
                fileLog(`${LOG_PREFIX}   Navigating to: ${url}`)
                router.push(url)
              }}
              disabled={!holdIdRef.current || holdLoading}
              className="w-full flex items-center justify-center gap-2 bg-[var(--main-color)] hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Weiter zur Buchung – {formatPrice(effectiveBoersePrice * seats)} <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Haupt-Seite ──

export default function BoersePage() {
  const heroData = pageHeroData.boerse
  const [data, setData] = useState<BoerseSlotsResponse | null>(null)
  const [boerseConfig, setBoerseConfig] = useState<BoersePublicConfig | null>(null)
  const [arrivalMinutesBefore, setArrivalMinutesBefore] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval>>()
  const fetchEpochRef = useRef(0) // Guard gegen veraltete Fetch-Responses

  const fetchSlots = useCallback(async () => {
    const epoch = ++fetchEpochRef.current
    fileLog("[BOERSE:Page] ════════════════════════════════════════════════════")
    fileLog("[BOERSE:Page] fetchSlots() CALLED, epoch:", epoch)
    try {
      const res = await api.getBoerseSlots()
      // Nur anwenden wenn kein neuerer Fetch losgetreten wurde
      if (fetchEpochRef.current !== epoch) {
        fileLog("[BOERSE:Page] fetchSlots() epoch", epoch, "DISCARDED (newer fetch active)")
        return
      }
      fileLog("[BOERSE:Page] fetchSlots() →", res.slots.length, "Slots loaded")
      fileLog("[BOERSE:Page] Config:", JSON.stringify(res.config))
      
      // Log slot details
      for (const s of res.slots) {
        fileLog(`[BOERSE:Page]   Slot ${s.slotId.slice(-8)}: remaining=${s.remainingSeats}, capacityHeld=${s.capacityHeld}, capacityTotal=${s.capacityTotal}, boersePrice=${s.boersePrice}`)
      }

      // Safety net: preserve ANY slot that we have an active hold on, even if
      // it disappeared from the response (e.g., remaining=0 after holding all seats).
      // Previously only preserved activeSlotIdImmRef, but user might have holds on
      // multiple slots that all need preserving.
      setData((prev) => {
        const ownSlots = ownHeldSlotsRef.current
        if (ownSlots.size === 0) return res

        // Find slots that we're holding but are missing from response
        const responseSlotIds = new Set(res.slots.map((s) => s.slotId))
        const missingOwnSlots = [...ownSlots.keys()].filter((id) => !responseSlotIds.has(id))

        if (missingOwnSlots.length === 0) return res

        // Preserve all missing slots from previous data
        const slotsToAdd: BoerseSlot[] = []
        for (const slotId of missingOwnSlots) {
          const preserved = prev?.slots.find((s) => s.slotId === slotId)
          if (preserved) {
            // Use our tracked seat count - the actual seats WE are holding
            const ourHeldSeats = ownSlots.get(slotId) || 0
            // Calculate remaining based on our held seats vs capacityTotal
            // Our held seats ARE available to us, so remaining = capacityTotal - otherHolds
            // But since slot disappeared (remaining=0), we only have our seats
            const correctedCapacityHeld = ourHeldSeats
            const correctedRemaining = preserved.capacityTotal - ourHeldSeats
            fileLog("[WARN]", `[Börse] Own slot ${slotId.slice(-4)} missing from response → preserving (ourHeldSeats: ${ourHeldSeats}, remaining: ${correctedRemaining})`)
            slotsToAdd.push({ ...preserved, remainingSeats: correctedRemaining, capacityHeld: correctedCapacityHeld })
          } else {
            fileLog("[ERROR]", `[Börse] Own slot ${slotId.slice(-4)} missing and not in prev data! Removing from ownHeldSlots.`)
            ownHeldSlotsRef.current.delete(slotId)
          }
        }

        if (slotsToAdd.length === 0) return res
        return { ...res, slots: [...res.slots, ...slotsToAdd] }
      })
      setError(null)
    } catch (e: unknown) {
      if (fetchEpochRef.current !== epoch) return
      setError(e instanceof Error ? e.message : "Fehler beim Laden der Slotbörse")
    } finally {
      if (fetchEpochRef.current === epoch) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    fetchSlots()
    api.getBoerseConfig().then(setBoerseConfig).catch(() => {})
    api.getPublicBookingSettings().then((s) => setArrivalMinutesBefore(s.arrivalMinutesBefore)).catch(() => {})
    // Fallback-Polling alle 60 s (WebSocket liefert Live-Updates)
    refreshTimerRef.current = setInterval(fetchSlots, 60_000)
    return () => clearInterval(refreshTimerRef.current)
  }, [fetchSlots])

  const slots = data?.slots ?? []
  const dropIntervalMin = data?.config?.dropIntervalMin ?? 10
  const maxSeats = data?.config?.maxSeatsPerBooking ?? 0
  const [activeTab, setActiveTab] = useState<string>("ALL")
  const [sortMode, setSortMode] = useState<"discount-percent" | "price-asc" | "start-time">("discount-percent")
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null)
  const [activeHoldId, setActiveHoldId] = useState<string | null>(null)

  // Sync-Refs: werden SOFORT aktualisiert (kein React-Render-Delay).
  // useBoerseSocket liest diese Refs direkt → WS-Events werden korrekt
  // unterdrückt, auch wenn sie VOR dem nächsten React-Render eintreffen.
  const activeHoldIdImmRef = useRef<string | null>(null)
  const activeSlotIdImmRef = useRef<string | null>(null)

  // Track ALL slots that have our own active holds (not just the "selected" one).
  // This ensures the safety net preserves ALL slots we hold, not just activeSlotId.
  const ownHeldSlotsRef = useRef<Map<string, number>>(new Map())

  // Externe Holds tracken: Map<slotId, Set<holdId>> — echtes Ref-Counting
  // damit bei mehreren Usern auf demselben Slot korrekt ausgegraut wird.
  const externalHoldsRef = useRef<Map<string, Set<string>>>(new Map())
  const externalHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [externallyHeldSlotIds, setExternallyHeldSlotIds] = useState<Set<string>>(new Set())

  const handleExternalHold = useCallback((holdId: string, slotIds: string[], type: "created" | "expired") => {
    const map = externalHoldsRef.current
    for (const slotId of slotIds) {
      if (type === "created") {
        if (!map.has(slotId)) map.set(slotId, new Set())
        map.get(slotId)!.add(holdId)
      } else {
        const set = map.get(slotId)
        if (set) {
          set.delete(holdId)
          if (set.size === 0) map.delete(slotId)
        }
      }
    }
    // State ableiten — bei "created" sofort anzeigen, bei "expired" verzögern.
    // Grund: Wenn ein anderer User seinen Hold aktualisiert (z.B. +1 Platz),
    // sendet der Server hold:expired(alt) + hold:created(neu) kurz nacheinander.
    // Ohne Debounce verschwindet das Overlay kurz (expired) und erscheint sofort
    // wieder (created) → visuelles "Zappeln". Mit Verzögerung auf expired wird
    // die Entfernung gebatcht und das Overlay bleibt stabil.
    if (externalHoldTimerRef.current) clearTimeout(externalHoldTimerRef.current)
    if (type === "created") {
      // Sofort: neuer Hold → Overlay direkt sichtbar
      externalHoldTimerRef.current = null
      setExternallyHeldSlotIds(new Set(map.keys()))
    } else {
      // Verzögert: expired → 300ms warten ob direkt ein created folgt
      externalHoldTimerRef.current = setTimeout(() => {
        externalHoldTimerRef.current = null
        setExternallyHeldSlotIds(new Set(map.keys()))
      }, 300)
    }
  }, [])

  // Live-Kapazitäts-Updates via WebSocket
  // Sync-Refs statt State → WS-Handler hat SOFORT den aktuellen Wert,
  // ohne auf einen React-Render-Zyklus warten zu müssen.
  useBoerseSocket(slots, fetchSlots, activeHoldIdImmRef, activeSlotIdImmRef, handleExternalHold)

  const handleActivate = useCallback((slotId: string, holdId: string, seats: number) => {
    const hid = holdId || null
    // Sync-Refs SOFORT updaten (vor dem nächsten Render)
    activeSlotIdImmRef.current = slotId
    activeHoldIdImmRef.current = hid
    // Track this slot with seat count (for safety net capacity calculation)
    ownHeldSlotsRef.current.set(slotId, seats)
    fileLog(`[BOERSE:Page] handleActivate: slotId=${slotId.slice(-4)}, holdId=${hid?.slice(-8) ?? 'null'}, seats=${seats}, ownHeldSlots=[${[...ownHeldSlotsRef.current.entries()].map(([s, n]) => `${s.slice(-4)}:${n}`).join(',')}]`)
    // State für UI-Rendering
    setActiveSlotId(slotId)
    setActiveHoldId(hid)
  }, [])

  const handleDeactivate = useCallback(() => {
    // Remove this slot from our held slots tracking
    if (activeSlotId) {
      ownHeldSlotsRef.current.delete(activeSlotId)
      fileLog(`[BOERSE:Page] handleDeactivate: removed ${activeSlotId.slice(-4)} from ownHeldSlots, remaining=[${[...ownHeldSlotsRef.current.keys()].map(s => s.slice(-4)).join(',')}]`)
      // Eigene SlotId aus externem Tracking entfernen
      externalHoldsRef.current.delete(activeSlotId)
      setExternallyHeldSlotIds(new Set(externalHoldsRef.current.keys()))
    }
    // Sync-Refs SOFORT clearen
    activeSlotIdImmRef.current = null
    activeHoldIdImmRef.current = null
    setActiveSlotId(null)
    setActiveHoldId(null)
  }, [activeSlotId])

  // Called when a card releases its hold (cancel or cleanup) - removes slot from ownHeldSlotsRef
  const handleHoldReleased = useCallback((slotId: string) => {
    ownHeldSlotsRef.current.delete(slotId)
    fileLog(`[BOERSE:Page] handleHoldReleased: removed ${slotId.slice(-4)} from ownHeldSlots, remaining=[${[...ownHeldSlotsRef.current.keys()].map(s => s.slice(-4)).join(',')}]`)
  }, [])

  // Eindeutige Track-Namen (Reihenfolge nach erstem Vorkommen = nächster Slot zuerst)
  const trackNames = [...new Map(slots.map((s) => [s.trackName, s.trackId])).keys()]

  // Sortierung: wählbar (Rabatt %, Preis €, Startzeit).
  // Wenn ein Slot aktiv reserviert ist, Reihenfolge einfrieren (keine Re-Sorts
  // durch WS-Refreshes), damit die Karten nicht springen.
  const sortedOrderRef = useRef<string[] | null>(null)

  const filteredSlots = useMemo(() => {
    const base = activeTab === "ALL"
      ? slots
      : slots.filter((s) => s.trackName === activeTab)

    if (activeSlotId && sortedOrderRef.current) {
      // Reihenfolge eingefroren: Slots nach gespeicherter Reihenfolge anordnen
      const orderMap = new Map(sortedOrderRef.current.map((id, i) => [id, i]))
      return base.slice().sort((a, b) => {
        const ai = orderMap.get(a.slotId) ?? 9999
        const bi = orderMap.get(b.slotId) ?? 9999
        return ai - bi
      })
    }

    // Effektiver Börse-Preis: bei VARIABLE die Default-Duration (längste), sonst boersePrice
    const getEffectiveBoersePrice = (s: BoerseSlot): number => {
      if (s.slotMode === "VARIABLE" && s.availableDurations?.length) {
        const dur = s.bookedRideDurationMin ?? Math.max(...s.availableDurations)
        const durKey = dur as unknown as keyof typeof s.boerseDurationPrices
        if (s.boerseDurationPrices && durKey in s.boerseDurationPrices) {
          return s.boerseDurationPrices[durKey]!
        }
      }
      return s.boersePrice
    }

    // Kein aktiver Hold → normal sortieren und Reihenfolge merken
    const sorted = base.slice().sort((a, b) => {
      switch (sortMode) {
        case "price-asc":
          // Günstigster Börse-Preis zuerst, Tiebreaker: höherer Rabatt, dann Startzeit
          return getEffectiveBoersePrice(a) - getEffectiveBoersePrice(b)
            || b.discountPercent - a.discountPercent
            || new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
        case "start-time":
          // Nächster Slot zuerst, Tiebreaker: höherer Rabatt
          return new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
            || b.discountPercent - a.discountPercent
        case "discount-percent":
        default:
          // Höchster Rabatt zuerst, Tiebreaker: günstigster Preis, dann Startzeit
          return b.discountPercent - a.discountPercent
            || getEffectiveBoersePrice(a) - getEffectiveBoersePrice(b)
            || new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
      }
    })
    sortedOrderRef.current = sorted.map((s) => s.slotId)
    return sorted
  }, [slots, activeTab, activeSlotId, sortMode])

  return (
    <>
      <PageHero title={heroData.title} description={heroData.description} backgroundImage={heroData.backgroundImage} />

      <div className="py-16">
        <div className="container-custom">

          {/* Erklärung */}
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-50 text-[var(--main-color)] rounded-full px-4 py-2 text-sm font-medium mb-4">
              <TrendingDown className="h-4 w-4" />
              Preise fallen alle {dropIntervalMin} Minuten
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Schnäppchen sichern – last minute fahren
            </h2>
            <p className="text-gray-600 leading-relaxed">
              In der Slotbörse findest du freie Rennzeiten zu reduzierten Preisen.
              Je näher der Startzeitpunkt rückt, desto günstiger wird es – aber die Plätze sind begrenzt!
              Schlage zu, bevor jemand anderes schneller ist.
            </p>
            {arrivalMinutesBefore > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl px-4 py-2.5 text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  Bitte stelle sicher, dass du mindestens <strong>{arrivalMinutesBefore} Minuten</strong> vor Beginn deines Slots auf der Kartbahn bist!
                </span>
              </div>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="h-8 w-8 text-[var(--main-color)] animate-spin" />
            </div>
          )}

          {/* Fehler */}
          {error && !loading && (
            <div className="max-w-lg mx-auto bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-700 font-medium">{error}</p>
              <button
                onClick={() => { setLoading(true); fetchSlots() }}
                className="mt-3 text-sm text-red-600 underline"
              >
                Erneut versuchen
              </button>
            </div>
          )}

          {/* Keine Slots */}
          {!loading && !error && slots.length === 0 && (
            <div className="max-w-lg mx-auto bg-gray-50 border border-gray-200 rounded-xl p-10 text-center">
              <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Aktuell keine Börse-Slots verfügbar</h3>
              <p className="text-gray-500 mb-6">
                Schau später nochmal vorbei – kurz vor den Rennzeiten tauchen hier regelmäßig Schnäppchen auf!
              </p>
              <Link
                href="/buchen"
                className="inline-flex items-center gap-2 bg-[var(--main-color)] hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl transition-colors"
              >
                Zur regulären Buchung
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          )}

          {/* Slot-Karten nach Track */}
          {!loading && !error && slots.length > 0 && (
            <div className="space-y-8">

              {/* ── Track-Tabs + Sortierung ── */}
              <div className="flex items-center gap-3">
                {/* Tabs (scrollbar bei vielen Bahnen) */}
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide min-w-0 flex-1">
                  <button
                    onClick={() => setActiveTab("ALL")}
                    className={[
                      "shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all border",
                      activeTab === "ALL"
                        ? "bg-[var(--main-color)] text-white border-[var(--main-color)]"
                        : "bg-white text-gray-600 border-gray-200 hover:border-[var(--main-color)] hover:text-[var(--main-color)]",
                    ].join(" ")}
                  >
                    Alle Bahnen
                    <span className="ml-1.5 text-xs opacity-70">({slots.length})</span>
                  </button>
                  {trackNames.map((name) => {
                    const count = slots.filter((s) => s.trackName === name).length
                    return (
                      <button
                        key={name}
                        onClick={() => setActiveTab(name)}
                        className={[
                          "shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all border",
                          activeTab === name
                            ? "bg-[var(--main-color)] text-white border-[var(--main-color)]"
                            : "bg-white text-gray-600 border-gray-200 hover:border-[var(--main-color)] hover:text-[var(--main-color)]",
                        ].join(" ")}
                      >
                        {name}
                        <span className="ml-1.5 text-xs opacity-70">({count})</span>
                      </button>
                    )
                  })}
                </div>

                {/* Sortierung (rechts) */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <ArrowUpDown className="h-4 w-4 text-gray-400" />
                  <select
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value as typeof sortMode)}
                    disabled={!!activeSlotId}
                    className="text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--main-color)] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <option value="discount-percent">Rabatt (%)</option>
                    <option value="price-asc">Preis (€)</option>
                    <option value="start-time">Startzeit</option>
                  </select>
                </div>
              </div>

              {/* ── Slot-Grid ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSlots.map((slot) => (
                  <BoerseSlotCard
                    key={slot.slotId}
                    slot={slot}
                    maxSeats={maxSeats}
                    onRefresh={fetchSlots}
                    activeSlotId={activeSlotId}
                    onActivate={handleActivate}
                    onDeactivate={handleDeactivate}
                    onHoldReleased={handleHoldReleased}
                    isExternallyHeld={
                      externallyHeldSlotIds.has(slot.slotId)
                      || (slot.capacityHeld > 0 && slot.slotId !== activeSlotId)
                    }
                  />
                ))}
              </div>

              {/* Refreshed-at Hinweis */}
              <div className="text-center text-xs text-gray-400 mt-8">
                Plätze werden in Echtzeit aktualisiert.{" "}
                <button
                  onClick={() => { setLoading(true); fetchSlots() }}
                  className="underline hover:text-gray-600"
                >
                  Jetzt aktualisieren
                </button>
              </div>
            </div>
          )}

          {/* Info-Banner */}
          <div className="mt-16 bg-gradient-to-r from-gray-50 to-orange-50 rounded-2xl border border-gray-200 p-8 md:p-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4">So funktioniert die Slotbörse</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-[var(--main-color)] text-white rounded-full flex items-center justify-center font-bold text-lg shrink-0">1</div>
                <div>
                  <p className="font-medium text-gray-900">Slot auswählen</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Suche dir einen freien Slot aus – der angezeigte Preis ist der aktuelle Echtzeit-Preis.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-[var(--main-color)] text-white rounded-full flex items-center justify-center font-bold text-lg shrink-0">2</div>
                <div>
                  <p className="font-medium text-gray-900">Abwarten oder zuschlagen</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Der Preis sinkt alle {dropIntervalMin} Minuten weiter. Aber Vorsicht: andere können schneller sein!
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-[var(--main-color)] text-white rounded-full flex items-center justify-center font-bold text-lg shrink-0">3</div>
                <div>
                  <p className="font-medium text-gray-900">Buchen & sparen</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Buche zum aktuellen Börsenpreis und spare gegenüber dem regulären Tarif.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  )
}
